import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import QRCodeDisplay from "../components/QRCodeDisplay";
import { useQRCode } from "../hooks/datakeeper/useQRCode";
import { useParams } from "react-router-dom";
import { BoxPaper } from "./Screening";
import { useSubmitWithoutDigiD } from "../hooks/datakeeper/useSubmitWithoutDigiD";
import theme from "../styles/styleTheme";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { useProperty } from "../hooks/properties/useProperty";
import { useTranslation } from "react-i18next";
import { useDatakeeperProcessed } from "../hooks/datakeeper/useDatakeeperProcessed";
import { isMobile } from "react-device-detect";

const Datakeeper = () => {
  const { encodedJson } = useParams();
  const { t } = useTranslation();
  const [hasDigiD, setHasDigiD] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [ submittedInterval, setSubmittedInterval ] = useState<any | null>(null);
  const { getDataById: getQRCodeData, data: qrCodeData, loading } = useQRCode();
  const { getData: getProcessed, data: processed, loading: processedLoading } = useDatakeeperProcessed();
  const { submitWithoutDigiD, loading: loadSubmit } = useSubmitWithoutDigiD();
  const { getDataById: getPropertyDataById, data: propertyData } =
    useProperty();
  const [includesHousingExpensesCheck, setIncludesHousingExpensesCheck] = useState(false);

  useEffect(() => {
    if (isMobile && qrCodeData?.url) {
      // For Android Intent URLs
      if (qrCodeData.url.startsWith('intent://')) {
        window.location.replace(qrCodeData.url);
      } else {
        window.location.href = qrCodeData.url;
      }
    }
  }, [qrCodeData]);

  const paramData = encodedJson ? JSON.parse(atob(encodedJson)) : "";

  useEffect(() => {
    getPropertyDataById(paramData.propertyId);

    const payload = {
      propertyId: paramData.propertyId,
      tenantId: paramData.tenantId,
      email: paramData.email,
    };

    const checkProcessed = async () => {
      setHasSubmitted((await getProcessed(payload)));
    }

    checkProcessed();
    setSubmittedInterval(
      setInterval(async () => {
        if (!hasSubmitted) {
          const submitted = await getProcessed(payload);
          setHasSubmitted(submitted);
        }
      } , 5000)
    );
  }, []);

  useEffect(() => {
    if (hasSubmitted) {
      clearInterval(submittedInterval);
    } 
  }, [hasSubmitted]);

  useEffect(() => {
    if (hasDigiD) {
      getQRCodeData({
        propertyId: paramData.propertyId,
        tenantId: paramData.tenantId,
        email: paramData.email,
        includesHousingExpensesCheck: includesHousingExpensesCheck
      });
    }
  }, [hasDigiD]);

  const handleRadioChange = (event: any) => {
    setSelectedOption(event.target.value);
  };

  const hanldeSubmit = () => {
    setHasSubmitted(true);
    submitWithoutDigiD(paramData.tenantId, paramData.email);
  };

  useEffect(() => {
    if (propertyData) {
      // Usecase for Urban interest, they have a different QR code
      if (propertyData?.landlordEntity?.landlordId === 291) {
        setIncludesHousingExpensesCheck(true);
      }
    }
  }, [propertyData]);

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      justifyContent="flex-start"
      alignItems="center"
      sx={{ minHeight: "30vh" }}
    >
      <BoxPaper
        style={{
          maxWidth: "50rem",
          display: "flex",
          flexDirection: "column",
          padding: "2rem",
        }}
      >
        <Box marginBottom={2}>
          <Typography variant="h1">
            {t("datakeeper.title")}: {propertyData?.title}
          </Typography>
        </Box>
        {hasSubmitted ? (
          <Typography variant="body1">
            {t("datakeeper.completed")}            
          </Typography>
        ) : (
          <>
            <Typography variant="body1" marginBottom={2}>
              {t("datakeeper.intro")}
            </Typography>
            <Box>
              <FormControl>
                <RadioGroup
                  aria-label="digid"
                  name="digid-radio-buttons"
                  row
                  onChange={handleRadioChange}
                >
                  <FormControlLabel
                    value="hasDigiD"
                    control={<Radio />}
                    label={t("datakeeper.digid.yes")}
                    onChange={() => setHasDigiD(true)}
                    disabled={hasSubmitted}
                  />
                  <FormControlLabel
                    value="noDigiD"
                    control={<Radio />}
                    label={t("datakeeper.digid.no")}
                    onChange={() => setHasDigiD(false)}
                    disabled={hasSubmitted}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </>
        )}

        {selectedOption === "hasDigiD" && !hasSubmitted && (
          <Box
            sx={{ marginBottom: 2, marginTop: 2 }}
            display={"flex"}
            flexDirection={"column"}
          >
            {loading ? (
              <CircularProgress />
            ) : (
              <>
                {isMobile ? (
                  <Typography variant="body1">Redirecting...</Typography>
                ) : (
                  <>
                    <QRCodeDisplay
                      base64String={qrCodeData.qrCode}
                      altText="Datakeeper QR code"
                      sx={{ width: "17rem" }}
                    />
                    <Box sx={{ marginBottom: 2, marginTop: 4 }} maxWidth="90%">
                      <Typography variant="body2">
                      {t("datakeeper.qrcode")}
                      </Typography>
                    </Box>
                  </>)
                }
              </>
            )}
          </Box>
        )}

        {selectedOption === "noDigiD" && !hasSubmitted && (
          <>
            <Box
              marginTop={3}
              sx={{
                backgroundColor: theme.palette.secondary.light,
                border: "1px solid " + theme.palette.secondary.main,
                padding: "1rem",
                borderRadius: "10px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "1rem",
              }}
            >
              <ReportProblemOutlinedIcon color="secondary" />
              <Box>
                <Typography
                  variant="body1"
                  color={theme.palette.secondary.main}
                  fontWeight={"bold"}
                >
                  {t("datakeeper.noDigid1")}
                </Typography>
                <Typography
                  color={theme.palette.secondary.main}
                  fontWeight={"bold"}
                >
                  {t("datakeeper.noDigid2")}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={() => hanldeSubmit()}
              sx={{ marginTop: 5 }}
            >
              {t("common.confirm")}
            </Button>
          </>
        )}

        {hasSubmitted && (
          <Box
            marginTop={3}
            sx={{
              backgroundColor: theme.palette.secondary.light,
              border: "1px solid " + theme.palette.secondary.main,
              padding: "1rem",
              borderRadius: "10px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            {loadSubmit ? (
              <CircularProgress />
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <CheckIcon color="secondary" />
                <Typography
                  color={theme.palette.secondary.main}
                  fontWeight={"bold"}
                >
                  {t("datakeeper.success")}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </BoxPaper>
    </Box>
  );
};

export default Datakeeper;
